import { Assets, Rectangle, Texture } from 'pixi.js';
import { type Direction, ALL_DIRECTIONS } from '../sim/direction';
import {
  actions as actionDefs,
  bodies,
  actionsForBody,
  equipment as equipDefs,
  DUEL_CLIP_IDS,
  COMBAT_STANCE_CLIP_IDS,
  EMOTE_CLIP_IDS,
  CAST_CLIP_IDS,
  SHOOT_CLIP_IDS,
  WEAPON_SWING_CLIP_IDS,
  GHOST_BODY_MALE,
  GHOST_BODY_FEMALE,
  type Registry,
  type ActionDefinition,
} from '../sim/content';
import type { BakedManifest } from './bakedManifest';
import { LazyBakedManifestStore } from './lazyBakedManifest';
import { byBaseName, mobileAtlasMap, mobileManifestStore } from './mobileAssetUrls';
// Body 400 (male) locomotion atlases.
import walkManifestJson from '../../assets/baked/mobiles/human-400-walk.json';
import walkAtlasUrl from '../../assets/baked/mobiles/human-400-walk.webp?url';
import runManifestJson from '../../assets/baked/mobiles/human-400-run.json';
import runAtlasUrl from '../../assets/baked/mobiles/human-400-run.webp?url';
import standManifestJson from '../../assets/baked/mobiles/human-400-stand.json';
import standAtlasUrl from '../../assets/baked/mobiles/human-400-stand.webp?url';
import fidgetManifestJson from '../../assets/baked/mobiles/human-400-fidget.json';
import fidgetAtlasUrl from '../../assets/baked/mobiles/human-400-fidget.webp?url';
// Body 401 (female) locomotion atlases.
import walk401ManifestJson from '../../assets/baked/mobiles/human-401-walk.json';
import walk401AtlasUrl from '../../assets/baked/mobiles/human-401-walk.webp?url';
import run401ManifestJson from '../../assets/baked/mobiles/human-401-run.json';
import run401AtlasUrl from '../../assets/baked/mobiles/human-401-run.webp?url';
import stand401ManifestJson from '../../assets/baked/mobiles/human-401-stand.json';
import stand401AtlasUrl from '../../assets/baked/mobiles/human-401-stand.webp?url';
import fidget401ManifestJson from '../../assets/baked/mobiles/human-401-fidget.json';
import fidget401AtlasUrl from '../../assets/baked/mobiles/human-401-fidget.webp?url';

/** The UO body ids the game bakes and can render: 400 male, 401 female. */
export const MALE_BODY = 400;
export const FEMALE_BODY = 401;
/** The body used when an entity carries no explicit body id. */
export const DEFAULT_BODY = MALE_BODY;

/** Clips required to draw a character before any one-shot action is requested. */
export const LOCOMOTION_CLIP_IDS = ['walk', 'run', 'stand', 'fidget'] as const;
/** Extra gait clips required whenever a visible look carries something in one hand. */
export const ARMED_LOCOMOTION_CLIP_IDS = ['walk-armed', 'run-armed'] as const;

/** One animation frame: its atlas sub-texture plus the UO per-frame anchor (px). */
export interface FrameTexture {
  texture: Texture;
  cx: number;
  cy: number;
}

/** The frames of one action, indexed by facing (frame 0 is the rest pose). */
export type DirectionFrames = Record<Direction, FrameTexture[]>;

/**
 * Every animation clip a mobile can play, each a per-facing frame list. `walk`
 * and `run` are the two gaits (run is the faster, distinct UO run cycle);
 * `stand` is a single static rest frame per facing the renderer holds while
 * idle, and `fidget` plays through once now and then on top of it.
 *
 * `attack` and `die` are the **one-shot action clips** the Rat Swarm event
 * reintroduced (ADR-0116, retiring ADR-0001's locomotion-only stance): the
 * player's "bow lesser" stomp swing and the rat's death. The **duel clips**
 * (`combat-idle`/`combat-advance`/`punch-1..3`/`hit`/`die-forward`/`die-back`;
 * PRD #252 slice B, ADR-0129) extend the same idea for player duels. They are all
 * optional - absent art degrades gracefully, the sprite simply holds its stand
 * pose when asked to play a clip it does not have - and are played once on an
 * `ActionAnimation` broadcast via {@link PlayerSprite.playAction}, never by the
 * locomotion state machine. The keys with hyphens are quoted; every optional clip
 * is looked up by its wire name through {@link PlayerSprite}'s dynamic `clipByName`,
 * so this field list is documentary (the render path never hard-codes them).
 */
export interface PlayerFrames {
  walk: DirectionFrames;
  run: DirectionFrames;
  stand: DirectionFrames;
  fidget: DirectionFrames;
  attack?: DirectionFrames;
  die?: DirectionFrames;
  'combat-idle'?: DirectionFrames;
  'combat-advance'?: DirectionFrames;
  'punch-1'?: DirectionFrames;
  'punch-2'?: DirectionFrames;
  'punch-3'?: DirectionFrames;
  hit?: DirectionFrames;
  'die-forward'?: DirectionFrames;
  'die-back'?: DirectionFrames;
  'ride-slow'?: DirectionFrames;
  'ride-fast'?: DirectionFrames;
  'ride-stand'?: DirectionFrames;
}

/**
 * A **mounted view** of one layer's clips (ADR-0442): the same {@link PlayerFrames}
 * with the three locomotion clips substituted for their seated counterparts, so the
 * layer's `walk` is the rider's {@link RIDE_CLIP_IDS `ride-slow`}, its `run` is
 * `ride-fast`, and its `stand` is `ride-stand`.
 *
 * This is the whole of "being mounted" on the render side, and the reason
 * {@link PlayerSprite} needs no mount-specific mode: the sprite's shared clock already
 * picks `walk`/`run` while gaiting and `stand` at rest, so re-pointing those three
 * names at the seated art makes the entire existing state machine - gait cadence, the
 * run/walk swap, the per-layer frame clamp - drive a rider correctly for free. Applied
 * per layer, so the rider's body AND every worn cosmetic ride together (equipment
 * bakes the people action table, mounted groups included).
 *
 * `fidget` is deliberately emptied rather than substituted: UO has no mounted fidget,
 * and leaving the standing shift/stretch in would make a seated rider stand up in the
 * saddle every few seconds. An empty clip makes {@link PlayerSprite}'s idle path hold
 * the mounted stand pose, which is exactly UO's behaviour.
 *
 * Every substitution falls back to the unmounted clip when the seated art is absent -
 * a clientless stub host, or a rider whose ride clips are still streaming in - so an
 * unbaked mount degrades to an ordinary standing figure on a camel rather than to a
 * blank sprite. The one-shot action clips are left untouched: a mounted rider still
 * bows, casts, and dies off their normal art.
 */
export function mountedClips(clips: PlayerFrames): PlayerFrames {
  // Degrade the whole seated set together when it is only clientless stub art, so a
  // dev/CI host draws an ordinary standing figure rather than three flat boxes.
  //
  // The test keys on `ride-slow`, NOT on each clip in turn, and that is deliberate:
  // a REAL `ride-stand` is genuinely one frame per facing (UO's Horse_Idle_01 is a
  // still pose - confirmed by decoding body 400 group 25 against client data), so
  // {@link isPlaceholderClip} cannot tell real mounted-idle art from a stub box and
  // would drop the correct clip on a fully-baked host. `ride-slow` has no such
  // ambiguity - the real walking seat is multi-frame - and the three clips are baked
  // as a set from one action table, so a stubbed `ride-slow` means all three are stubs.
  const walking = clips['ride-slow'];
  if (!walking || isPlaceholderClip(walking)) return clips;
  return {
    ...clips,
    walk: walking,
    run: clips['ride-fast'] ?? walking,
    stand: clips['ride-stand'] ?? clips.stand,
    fidget: {} as DirectionFrames,
  };
}

/**
 * Map a UO animation direction index (0..7, already de-mirrored by the converter)
 * to this game's compass {@link Direction}. UO stores 5 facings and mirrors 5-7
 * from 1-3, so the index runs as a clean clockwise rotation: anim dir 4 is the
 * back (N), dir 6 the right profile (E), dir 0 the front (S), dir 2 the left
 * profile (W). In our N-clockwise enum that is `(animDir + 4) mod 8`. (A
 * reflection like `12 - animDir` is wrong: it leaves N/S right but swaps E/W and
 * the diagonals.) If a facing ever looks wrong when walking, this is the line.
 */
function facingForAnimDir(animDir: number): Direction {
  return ALL_DIRECTIONS[(animDir + 4) % 8]!;
}

/**
 * Load one baked atlas (body 400, one action) and slice it into per-facing frame
 * lists. The atlas + manifest come from the offline `anim.mul` converter, so
 * frames carry real transparency and per-frame anchors (no white-key hack, no
 * swim). Resolves once the atlas texture is ready so the renderer has no race.
 */
async function loadAction(manifest: BakedManifest, atlasUrl: string): Promise<DirectionFrames> {
  const atlas = await Assets.load<Texture>(atlasUrl);
  atlas.source.scaleMode = 'nearest';

  const frames = {} as DirectionFrames;
  for (const dir of manifest.directions) {
    if (dir.frames.length === 0) continue;
    frames[facingForAnimDir(dir.direction)] = dir.frames.map((f) => ({
      texture: new Texture({ source: atlas.source, frame: new Rectangle(f.x, f.y, f.w, f.h) }),
      cx: f.cx,
      cy: f.cy,
    }));
  }
  return frames;
}

/** Every body's {@link PlayerFrames}, keyed by UO body id (400/401/215/402/403/164/991). */
export type BodyFrames = Record<number, PlayerFrames>;

// The baked mobile atlases + manifest URLs beyond the statically-imported human
// locomotion set: the optional clips (the ADR-0116 attack swing, the giant-rat
// body). Glob'd like the equipment atlases because the set is registry-driven -
// the stub (or the real converter) fills `assets/baked/mobiles/` by walking the
// SAME `mobileBakePlan`, so what can be indexed here never drifts from what is
// baked. Only the small URL index enters JavaScript; each manifest payload and PNG
// is fetched on first clip use. A clip absent on disk simply loads as `undefined` (graceful
// degrade: the sprite holds its stand pose instead).
/**
 * Load one optional mobile clip (`mobiles/human-<body>-<action>`), or `undefined`
 * when it is not baked - the degrade path for clips the converter/stub has not
 * produced (an unbaked attack swing, a missing rat clip). Required locomotion
 * clips stay statically imported above so their absence is still a loud build
 * error, not a silent hole.
 */
async function loadOptionalClip(body: number, action: string): Promise<DirectionFrames | undefined> {
  const key = `human-${body}-${action}`;
  const [manifest, url] = await Promise.all([
    mobileManifestStore.load(key),
    Promise.resolve(mobileAtlasMap.get(key)),
  ]);
  if (!manifest || !url) return undefined;
  return loadAction(manifest, url);
}

/**
 * The four people locomotion clips that are **statically imported** (their
 * absence is a loud build error). Every OTHER people action in the `actions`
 * registry - the ADR-0116 `attack` swing and the ADR-0129 duel clips
 * (combat-idle/advance, punches, hit, deaths) - is loaded **optionally** by
 * {@link loadOptionalPeopleClips}, degrading to the stand pose when unbaked.
 */
const STATIC_PEOPLE_CLIPS: ReadonlySet<string> = new Set(LOCOMOTION_CLIP_IDS);

function resolveBodyOptionalClip(
  actionId: string,
  frames: DirectionFrames,
  stand: DirectionFrames,
): DirectionFrames | undefined {
  if (
    isPlaceholderEmoteClip(actionId, frames) ||
    isPlaceholderCastClip(actionId, frames) ||
    isPlaceholderShootClip(actionId, frames) ||
    isPlaceholderWeaponClip(actionId, frames)
  ) {
    return undefined;
  }
  return resolveBodyDuelClip(actionId, frames, stand);
}

/**
 * Load every **optional** people clip for a body - i.e. every action in the
 * `actions` content registry beyond the four statically-imported locomotion
 * clips (the ADR-0116 attack swing + the ADR-0129 duel combat/punch/hit/die
 * clips). Registry-driven so it never drifts from what the converter/stub bake:
 * whatever the shared `actions` registry declares is loaded here, and a clip
 * with no baked atlas simply loads as `undefined` and is dropped (the sprite
 * holds its stand pose when asked to play it). Returns only the clips that are
 * actually baked, keyed by wire clip name.
 */
async function loadOptionalPeopleClips(
  body: number,
  stand: DirectionFrames,
): Promise<Partial<PlayerFrames>> {
  const optional = actionDefs.all().filter((a) => !STATIC_PEOPLE_CLIPS.has(a.id));
  const loaded = await Promise.all(
    optional.map(async (a) => [a.id, await loadOptionalClip(body, a.id)] as const),
  );
  const out: Record<string, DirectionFrames> = {};
  for (const [id, frames] of loaded) {
    if (!frames) continue;
    // A placeholder emote stub (bow/salute/dances, ADR-0144/0387) is a gesture with no
    // persistent stance to keep, so - like the punch/hit/die duel stubs - it is dropped
    // to the stand pose rather than floated. Restore the war-mode combat STANCE (ADR-0140 re-fix): a
    // stub combat-idle/advance is re-grounded to the stand pose so the stance renders on
    // the floor, not floating; every OTHER placeholder duel stub is still dropped. See
    // {@link resolveBodyDuelClip} / {@link isPlaceholderEmoteClip}.
    const resolved = resolveBodyOptionalClip(id, frames, stand);
    if (resolved) out[id] = resolved;
  }
  return out as Partial<PlayerFrames>;
}

/**
 * Load every clip body 400 (male) can play - the statically-imported walk + run
 * + stand + fidget, plus every optional people clip (the ADR-0116 attack swing
 * and the ADR-0129 duel clips), each baked from `anim.mul`. All are baked from
 * the actions declared in the content registry, so this stays in lockstep with
 * what the converter produces.
 */
async function loadMaleLocomotionFrames(): Promise<PlayerFrames> {
  const stand = await loadAction(standManifestJson as BakedManifest, standAtlasUrl);
  const [walk, run, fidget] = await Promise.all([
    loadAction(walkManifestJson as BakedManifest, walkAtlasUrl),
    loadAction(runManifestJson as BakedManifest, runAtlasUrl),
    loadAction(fidgetManifestJson as BakedManifest, fidgetAtlasUrl),
  ]);
  return { walk, run, stand, fidget };
}

export async function loadPlayerFrames(): Promise<PlayerFrames> {
  const locomotion = await loadMaleLocomotionFrames();
  const optional = await loadOptionalPeopleClips(MALE_BODY, locomotion.stand);
  return { ...locomotion, ...optional };
}

/** Load every clip body 401 (female) can play - the second baked body. */
async function loadFemaleLocomotionFrames(): Promise<PlayerFrames> {
  const stand = await loadAction(stand401ManifestJson as BakedManifest, stand401AtlasUrl);
  const [walk, run, fidget] = await Promise.all([
    loadAction(walk401ManifestJson as BakedManifest, walk401AtlasUrl),
    loadAction(run401ManifestJson as BakedManifest, run401AtlasUrl),
    loadAction(fidget401ManifestJson as BakedManifest, fidget401AtlasUrl),
  ]);
  return { walk, run, stand, fidget };
}

async function loadFemaleFrames(): Promise<PlayerFrames> {
  const locomotion = await loadFemaleLocomotionFrames();
  const optional = await loadOptionalPeopleClips(FEMALE_BODY, locomotion.stand);
  return { ...locomotion, ...optional };
}

/**
 * Load a non-people creature body's clips from **its own** action table - the giant
 * rat (animal set, ADR-0116), the energy vortex (monster set), and Lord Blackthorn
 * (blackthorn set), the last two the Blackthorn's Storm event bodies (ADR-0152).
 * Every clip is optional: a host where none are baked (converter never run, stubs
 * pre-date the body) returns `undefined`, and the body falls back to the default-body
 * rendering via {@link buildPlayerLayers}'s body fallback. These creature sets carry
 * no distinct run or fidget bake, so `run` reuses the walk cycle and `fidget` is empty
 * (the sprite holds the stand pose instead). The one-shot clips (the rat's `die`, the
 * vortex's `attack-*`/`materialize`/`die`, Blackthorn's `summon`) are carried through
 * under their wire clip names and played via {@link PlayerSprite}'s dynamic
 * `clipByName`, exactly like the duel clips. Registry-driven off the passed action
 * table, so what is loaded never drifts from what {@link mobileBakePlan} bakes.
 */
async function loadCreatureFrames(
  body: number,
  actionSet: Registry<ActionDefinition>,
): Promise<PlayerFrames | undefined> {
  const loaded = await Promise.all(
    actionSet.all().map(async (a) => [a.id, await loadOptionalClip(body, a.id)] as const),
  );
  const clips: Record<string, DirectionFrames> = {};
  for (const [id, frames] of loaded) {
    if (frames) clips[id] = frames;
  }
  if (Object.keys(clips).length === 0) return undefined;
  const stand = clips.stand ?? ({} as DirectionFrames);
  const walk = clips.walk ?? stand;
  return { ...clips, walk, run: walk, stand, fidget: {} as DirectionFrames } as PlayerFrames;
}

async function loadCreatureLocomotionFrames(
  body: number,
  actionSet: Registry<ActionDefinition>,
): Promise<PlayerFrames | undefined> {
  const locomotion = actionSet.all().filter((action) => STATIC_PEOPLE_CLIPS.has(action.id));
  const loaded = await Promise.all(
    locomotion.map(async (action) => [action.id, await loadOptionalClip(body, action.id)] as const),
  );
  const clips: Record<string, DirectionFrames> = {};
  for (const [id, frames] of loaded) {
    if (frames) clips[id] = frames;
  }
  if (Object.keys(clips).length === 0) return undefined;
  const stand = clips.stand ?? ({} as DirectionFrames);
  const walk = clips.walk ?? stand;
  return {
    ...clips,
    walk,
    run: clips.run ?? walk,
    stand,
    fidget: clips.fidget ?? ({} as DirectionFrames),
  } as PlayerFrames;
}

/**
 * Load the clips for **every** baked body - 400 male, 401 female, the giant rat, the
 * Blackthorn's Storm event bodies (the energy vortex 164, its chaos-llama variant 220,
 * and Lord Blackthorn 991), and the two duel ghost bodies - keyed by body id, so the
 * renderer can draw each character (or event/duel creature) in its own body. This is
 * the load surface `main.ts` uses; the crowd is visibly varied because each entity picks its body out
 * of this map. A storm body absent from this map would fall back to the default male
 * body (the "man 400" bug the Slice E client presentation left unwired, ADR-0152).
 *
 * A duel death swaps the loser's replicated `renderable.body` to a ghost id
 * (0x192/0x193, ADR-0129/0136), and the **corpse is the frozen final frame of the
 * die clip** held until resurrection. A UO human ghost has no art of its own (it maps
 * to the living body by subtracting 2, confirmed vs ClassicUO), so a ghost body
 * renders off its **correct-sex living body** frames - which carry the
 * `die-forward`/`die-back` clips (a female ghost 0x193 renders the female body, never
 * the default male). The held die frame survives the body swap; {@link
 * buildPlayerLayers} then dresses that ghost body as the solid, grayscale death shroud
 * (robe over the die pose). The ghost bodies stay registered and baked (non-fatally).
 */
export async function loadBodyFrames(): Promise<BodyFrames> {
  const [male, female] = await Promise.all([loadPlayerFrames(), loadFemaleFrames()]);
  const frames: BodyFrames = {
    [MALE_BODY]: male,
    [FEMALE_BODY]: female,
    [GHOST_BODY_MALE]: male,
    [GHOST_BODY_FEMALE]: female,
  };
  // Every registered non-people, non-ghost body loads its own clips through its
  // own action table ({@link loadCreatureBodyFrames}) - the giant rat, the Blackthorn's
  // Storm bodies (vortex/llama/Blackthorn), and the whole arena bestiary (ADR-0184).
  // Registry-driven, so a new creature body renders in its OWN body with zero further
  // wiring here. This eager whole-bestiary load is retained for tests/tools; the live
  // client streams each creature body on first sighting via the lazy store instead
  // (ADR-0247 / issue #474), so it never pays for bodies nobody is looking at.
  const creatures = bodies.all().filter((b) => b.actionSet && b.actionSet !== 'ghost');
  const loaded = await Promise.all(
    creatures.map(async (b) => [b.body, await loadCreatureFrames(b.body, actionsForBody(b))] as const),
  );
  for (const [body, creatureFrames] of loaded) {
    if (creatureFrames) frames[body] = creatureFrames;
  }
  return frames;
}

/**
 * Load only the **login-critical** body set: the two playable human bodies (400 male /
 * 401 female) and the two duel ghost aliases. This is what a fresh visitor needs to
 * render themselves and the human crowd at the bank; every OTHER body (the giant rat,
 * the Blackthorn's Storm bodies, the whole arena bestiary) is streamed on first
 * sighting via the lazy sprite-atlas store (ADR-0247 / issue #474), not eager-loaded
 * here. Ghost bodies have no art of their own (a UO human ghost maps to the living body
 * by subtracting 2, confirmed vs ClassicUO), so each aliases to its correct-sex living
 * body - the same mapping the full {@link loadBodyFrames} applies.
 */
export async function loadEssentialBodyFrames(): Promise<BodyFrames> {
  const [male, female] = await Promise.all([
    loadMaleLocomotionFrames(),
    loadFemaleLocomotionFrames(),
  ]);
  return {
    [MALE_BODY]: male,
    [FEMALE_BODY]: female,
    [GHOST_BODY_MALE]: male,
    [GHOST_BODY_FEMALE]: female,
  };
}

/**
 * Load **one** creature body's complete clip set by UO body id. Retained for tools and
 * compatibility callers; the live store uses {@link loadCreatureBodyLocomotionFrames}
 * plus {@link loadBodyActionFrames} instead.
 * Resolves the body's {@link BodyDefinition} out of the registry and loads its own
 * action table ({@link actionsForBody}), exactly as the eager {@link loadBodyFrames}
 * creature loop does per body. Returns `undefined` for a body that has no distinct
 * creature art to stream - an unregistered id, a people body (400/401, loaded eagerly
 * via the essential set), or a ghost body (which aliases to its living body) - so the
 * store leaves the renderer's default-body fallback in place for it.
 */
export async function loadCreatureBodyFrames(body: number): Promise<PlayerFrames | undefined> {
  const def = bodies.all().find((b) => b.body === body);
  if (!def || !def.actionSet || def.actionSet === 'ghost') return undefined;
  return loadCreatureFrames(body, actionsForBody(def));
}

/** Load only the first-paint locomotion clips for one non-human body. */
export async function loadCreatureBodyLocomotionFrames(
  body: number,
): Promise<PlayerFrames | undefined> {
  const def = bodies.all().find((candidate) => candidate.body === body);
  if (!def || !def.actionSet || def.actionSet === 'ghost') return undefined;
  return loadCreatureLocomotionFrames(body, actionsForBody(def));
}

/** Load one body action on demand after its locomotion frames are visible. */
export async function loadBodyActionFrames(
  body: number,
  actionId: string,
  stand: DirectionFrames,
): Promise<DirectionFrames | undefined> {
  const livingBody =
    body === GHOST_BODY_MALE ? MALE_BODY : body === GHOST_BODY_FEMALE ? FEMALE_BODY : body;
  const def = bodies.all().find((candidate) => candidate.body === livingBody);
  if (!def) return undefined;
  const actionSet = actionsForBody(def);
  if (!actionSet.all().some((action) => action.id === actionId)) return undefined;
  const frames = await loadOptionalClip(livingBody, actionId);
  if (!frames) return undefined;
  return livingBody === MALE_BODY || livingBody === FEMALE_BODY
    ? resolveBodyOptionalClip(actionId, frames, stand)
    : frames;
}

/**
 * Every equipment layer's {@link PlayerFrames}, keyed by content `equipment` id
 * (e.g. `hair.long`). An equipment layer is just *more anim graphics* drawn over
 * the body in lockstep with it, so it loads through the very same {@link
 * loadAction} path as a body - only the baked-asset folder differs.
 */
export type EquipFrames = Record<string, PlayerFrames>;

// The baked equipment atlases + manifest URLs, discovered the same way the body
// atlases are statically imported - but glob'd, because the set is registry-driven
// and grows with `content.ts` rather than being hand-listed. Vite resolves these
// at build/test time against `assets/baked/equip/`, which the stub (or the real
// converter) fills by walking the SAME `equipment` registry, so what is imported
// here can never drift from what is baked. Keyed by relative path; re-keyed to
// `<id>-<action>` basenames below. Manifest JSON remains a raw asset and is fetched
// only when that clip is requested.
const equipManifestUrls = import.meta.glob('../../assets/baked/equip/*.json', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;
const equipAtlasUrls = import.meta.glob('../../assets/baked/equip/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

// The `<id>-<action>`-keyed views of the equip globs, memoized: lazy per-equipment
// streaming (ADR-0247 / issue #474) resolves one atlas at a time via {@link
// loadEquipItemFrames}, so re-`byBaseName`-ing the whole glob per item would be O(n)
// work on every first-sighting. Built once on first use; the glob is build-time frozen.
let equipManifestStore: LazyBakedManifestStore | undefined;
let equipUrlMap: Map<string, string> | undefined;
function equipMaps(): { manifests: LazyBakedManifestStore; urls: Map<string, string> } {
  equipManifestStore ??= new LazyBakedManifestStore(byBaseName(equipManifestUrls));
  equipUrlMap ??= byBaseName(equipAtlasUrls);
  return { manifests: equipManifestStore, urls: equipUrlMap };
}

function resolveEquipActionClip(
  actionId: string,
  clip: DirectionFrames,
): DirectionFrames | undefined {
  if (
    isPlaceholderDuelClip(actionId, clip) ||
    isPlaceholderEmoteClip(actionId, clip) ||
    isPlaceholderCastClip(actionId, clip) ||
    isPlaceholderShootClip(actionId, clip) ||
    isPlaceholderWeaponClip(actionId, clip)
  ) {
    return undefined;
  }
  return clip;
}

/**
 * Detect a clientless single-frame stub from manifest metadata, before Pixi decodes
 * and caches its PNG. This is the manifest-side equivalent of
 * {@link isPlaceholderClip}.
 */
export function isPlaceholderManifest(manifest: BakedManifest): boolean {
  const directions = manifest.directions;
  return (
    Array.isArray(directions) &&
    directions.length > 0 &&
    directions.every((d) => d.frames.length <= 1)
  );
}

function shouldDropEquipManifest(actionId: string, manifest: BakedManifest): boolean {
  if (!isPlaceholderManifest(manifest)) return false;
  return (
    DUEL_CLIP_IDS.includes(actionId) ||
    EMOTE_CLIP_IDS.includes(actionId) ||
    CAST_CLIP_IDS.includes(actionId) ||
    SHOOT_CLIP_IDS.includes(actionId) ||
    WEAPON_SWING_CLIP_IDS.includes(actionId)
  );
}

async function loadEagerEquipAction(
  base: string,
  actionId: string,
  manifests: Map<string, BakedManifest>,
  urls: Map<string, string>,
  load: (manifest: BakedManifest, atlasUrl: string) => Promise<DirectionFrames> = loadAction,
): Promise<DirectionFrames | undefined> {
  const key = `${base}-${actionId}`;
  const manifest = manifests.get(key);
  const url = urls.get(key);
  if (!manifest || !url) return undefined;
  if (shouldDropEquipManifest(actionId, manifest)) return undefined;
  return resolveEquipActionClip(actionId, await load(manifest, url));
}

async function loadLazyEquipAction(
  base: string,
  actionId: string,
  manifests: LazyBakedManifestStore,
  urls: Map<string, string>,
): Promise<DirectionFrames | undefined> {
  const key = `${base}-${actionId}`;
  const [manifest, url] = await Promise.all([
    manifests.load(key),
    Promise.resolve(urls.get(key)),
  ]);
  if (!manifest || !url) return undefined;
  if (shouldDropEquipManifest(actionId, manifest)) return undefined;
  return resolveEquipActionClip(actionId, await loadAction(manifest, url));
}

/**
 * Assemble one layer's {@link PlayerFrames} from its baked per-action atlases,
 * looked up by `<base>-<action>` basename. The action field names come from the
 * `actions` content registry (the same set the converter bakes), so body and
 * equipment stay in lockstep; a missing action degrades to empty frames rather
 * than throwing (the renderer simply draws nothing for it).
 */
export async function assembleClips(
  base: string,
  manifests: Map<string, BakedManifest>,
  urls: Map<string, string>,
  load: (manifest: BakedManifest, atlasUrl: string) => Promise<DirectionFrames> = loadAction,
): Promise<PlayerFrames> {
  const frames = {} as Record<string, DirectionFrames>;
  await Promise.all(
    actionDefs.all().map(async (action) => {
      const loaded = await loadEagerEquipAction(base, action.id, manifests, urls, load);
      // Duel deathfix squares bug (ADR-0142): a duelist's worn items rendered as flat
      // boxes during combat/punch/die. The clientless stub bakes one placeholder box
      // per equipment × action; when the real converter has no combat frames for an
      // equipment anim graphic it skips that clip, leaving the stale single-frame stub
      // box, which the sprite then drew as a square during the duel actions. Drop a
      // duel-action clip that is only a single-frame placeholder so the sprite degrades
      // the layer to its stand/base pose (the existing, correct fallback) instead of a
      // box. Real multi-frame combat art (>1 frame) is kept, so clothing animates where
      // the captain's bake has it and never shows a square where it does not.
      // The same squares/float guard extends to the emote gestures (bow/salute/dances,
      // ADR-0144/0387): a placeholder emote stub on a clothed layer would draw the same
      // flat box, so drop it too - the layer degrades to its grounded stand pose.
      frames[action.id] = loaded ?? ({} as DirectionFrames);
    }),
  );
  // The keys come from the `actions` registry (walk/run/stand/fidget), which is
  // exactly PlayerFrames' shape, but TS can't see that from a dynamic record.
  return frames as unknown as PlayerFrames;
}

/**
 * Whether a loaded clip is a single-frame **placeholder** rather than a real
 * animation: it has at least one facing and every facing carries at most one frame.
 * The clientless stub bakes exactly one flat box per direction, whereas a real UO
 * combat clip is several frames - so this distinguishes a leftover stub box from
 * genuine art, used to drop duel-action equipment boxes that would otherwise render
 * as squares over a fighting duelist (Duel deathfix, ADR-0142).
 */
export function isPlaceholderClip(frames: DirectionFrames): boolean {
  const perDir = Object.values(frames) as FrameTexture[][];
  return perDir.length > 0 && perDir.every((f) => f.length <= 1);
}

/**
 * Whether a loaded clip should be **dropped** (degraded to the stand pose) because
 * it is only a leftover single-frame stub for a duel action (combat-idle/advance,
 * punches, hit, deaths - {@link DUEL_CLIP_IDS}). The clientless stub bakes one flat
 * 32×32 box per (mobile/equipment × action) with a full-tile feet anchor
 * (`cy = TILE-1`) that does NOT ground-register against the real, differently-
 * anchored stand/walk art; drawing it would lift the whole figure ~35px off the
 * ground - the duel "floating in war mode" regression. Real multi-frame combat art
 * (>1 frame) is never a placeholder, so a captain's real bake still animates. This is
 * only the **predicate**; the two load paths act on it differently (ADR-0140 re-fix):
 * {@link assembleClips} (equipment) **drops** every placeholder duel clip so the layer
 * degrades to its grounded stand pose (the "clothing squares" fix), while
 * {@link resolveBodyDuelClip} (body, via {@link loadOptionalPeopleClips}) re-grounds the
 * two combat-**stance** stubs to keep the war stance visible and drops only the one-shot
 * punch/hit/die stubs.
 */
export function isPlaceholderDuelClip(actionId: string, frames: DirectionFrames): boolean {
  return DUEL_CLIP_IDS.includes(actionId) && isPlaceholderClip(frames);
}

/**
 * Whether a loaded clip is only a leftover single-frame stub for a player **emote**
 * (bow / salute / dances - {@link EMOTE_CLIP_IDS}, ADR-0144/0387). Like the one-shot duel
 * stubs, an emote is a gesture with no persistent stance to preserve (a repeating one like
 * the dance is still just that gesture, played through a few times), so a placeholder
 * is **dropped** (degraded to the stand pose) on **both** render paths - the body
 * ({@link loadOptionalPeopleClips}) and equipment ({@link assembleClips}) - never
 * re-grounded like the war-mode combat stance. That keeps an unbaked host from floating
 * a stub box (the ADR-0140 "floating" regression) or drawing a clothing square (the
 * ADR-0142 squares regression) when a player emotes; a captain's real multi-frame bake
 * (>1 frame) is never a placeholder, so it animates as-is.
 */
export function isPlaceholderEmoteClip(actionId: string, frames: DirectionFrames): boolean {
  return EMOTE_CLIP_IDS.includes(actionId) && isPlaceholderClip(frames);
}

/**
 * Whether a loaded clip is only a leftover single-frame stub for a one-shot **skill
 * cast** (Spell1 or Spell2 - {@link CAST_CLIP_IDS}, ADR-0187 and ADR-0413).
 * Exactly the emote rule:
 * a fire-once swing with no persistent stance, so a placeholder is **dropped**
 * (degraded to the stand pose) on **both** render paths - never floated (ADR-0140)
 * or drawn as a clothing square (ADR-0142); a captain's real multi-frame bake
 * animates as-is.
 */
export function isPlaceholderCastClip(actionId: string, frames: DirectionFrames): boolean {
  return CAST_CLIP_IDS.includes(actionId) && isPlaceholderClip(frames);
}

/**
 * Whether a loaded clip is only a leftover single-frame stub for the arena archer's
 * **shots** (`shoot-bow` / `shoot-crossbow`, AttackBow/AttackCrossbow -
 * {@link SHOOT_CLIP_IDS}, PRD #446 Slice 1, ADR-0237). Exactly the cast/emote rule:
 * a fire-once shot with no persistent stance, so a placeholder is **dropped**
 * (degraded to the stand pose) on **both** render paths; a captain's real
 * multi-frame bake animates as-is.
 */
export function isPlaceholderShootClip(actionId: string, frames: DirectionFrames): boolean {
  return SHOOT_CLIP_IDS.includes(actionId) && isPlaceholderClip(frames);
}

/**
 * Whether a loaded clip is only a leftover single-frame stub for a **weapon swing**
 * (`attack-2h`, the King's Justice guard's halberd wack - {@link WEAPON_SWING_CLIP_IDS},
 * ADR-0194). Exactly the emote/cast rule: a fire-once swing with no persistent stance,
 * so a placeholder is **dropped** (degraded to the stand pose) on **both** render paths
 * - never floated (ADR-0140) or drawn as a clothing square (ADR-0142); a captain's real
 * multi-frame bake animates as-is.
 */
export function isPlaceholderWeaponClip(actionId: string, frames: DirectionFrames): boolean {
  return WEAPON_SWING_CLIP_IDS.includes(actionId) && isPlaceholderClip(frames);
}

/**
 * Re-anchor a placeholder combat-stance stub so it grounds on the stand pose's feet
 * line instead of floating (ADR-0140 re-fix). The clientless stub bakes a duel clip
 * with the full-tile feet anchor (`cy = TILE-1`), which does NOT ground-register against
 * the real, differently-anchored stand art - drawing it lifts the figure ~35px off the
 * floor. A layer's on-screen feet sit at `GROUND_OFFSET_Y - cy` (see {@link
 * PlayerSprite.applyFrame}), a function of `cy` alone, so copying the stand frame's
 * per-facing `cy` onto the stub drops its feet to exactly the resting ground line
 * regardless of the stub's (shorter) height. `cx` is left as the stub's own box-centre so
 * the placeholder stays horizontally centred. Facings the stand pose lacks are left
 * untouched (nothing to ground against).
 */
function groundStanceClipToStand(clip: DirectionFrames, stand: DirectionFrames): DirectionFrames {
  const out = {} as DirectionFrames;
  for (const dir of ALL_DIRECTIONS) {
    const frames = clip[dir];
    if (!frames || frames.length === 0) continue;
    const standCy = stand[dir]?.[0]?.cy;
    out[dir] = standCy === undefined ? frames : frames.map((f) => ({ ...f, cy: standCy }));
  }
  return out;
}

/**
 * Decide what a **body** layer does with one optional duel clip so the war-mode combat
 * stance shows grounded (ADR-0140 re-fix), returning the clip to keep or `undefined` to
 * drop:
 *  - real art (multi-frame) or any non-duel clip: kept unchanged (its own anchors already
 *    ground, so a captain's baked combat art animates as-is);
 *  - a placeholder **stance** stub (combat-idle / combat-advance, {@link
 *    COMBAT_STANCE_CLIP_IDS}): re-grounded to the stand pose ({@link
 *    groundStanceClipToStand}) and kept, so the persistent war stance stays visible on
 *    the ground line rather than floating or vanishing;
 *  - every other placeholder duel stub (punch / hit / die): dropped (`undefined`) - the
 *    corpse/ghost split (ADR-0140) and the clothing-squares fix (ADR-0142) both require
 *    those to be absent, and only the stance is meant to persist through a fight.
 *
 * Body-only: the equipment path ({@link assembleClips}) still drops *every* placeholder
 * duel stub, so a duelist's clothing never renders a flat-colour square in combat.
 */
export function resolveBodyDuelClip(
  actionId: string,
  frames: DirectionFrames,
  stand: DirectionFrames,
): DirectionFrames | undefined {
  if (!isPlaceholderDuelClip(actionId, frames)) return frames;
  if (COMBAT_STANCE_CLIP_IDS.includes(actionId)) return groundStanceClipToStand(frames, stand);
  return undefined;
}

/**
 * Load every baked equipment layer's clips, keyed by content id, so the renderer
 * can stack a character's chosen equipment over its body. Walks the same
 * `equipment` registry the converter/stub bake from, so every registered id is
 * present in the map; an id with no baked atlas for an action just gets empty
 * clips there, so a partially baked set never crashes the load. The renderer
 * (createSprite) skips an equipped item whose id has no entry in this map at all.
 */
export async function loadEquipFrames(): Promise<EquipFrames> {
  const result: EquipFrames = {};
  await Promise.all(
    equipDefs.all().map(async (def) => {
      result[def.id] = await loadEquipItemFrames(def.id);
    }),
  );
  return result;
}

/**
 * Load **one** equipment layer's complete {@link PlayerFrames} by content id.
 * Retained for tools and compatibility callers; the live store uses locomotion plus
 * individual action loads below.
 */
export async function loadEquipItemFrames(id: string): Promise<PlayerFrames> {
  const { manifests, urls } = equipMaps();
  const loaded = await Promise.all(
    actionDefs.all().map(async (action) => [
      action.id,
      await loadLazyEquipAction(id, action.id, manifests, urls),
    ] as const),
  );
  const clips: Record<string, DirectionFrames> = {};
  for (const [actionId, frames] of loaded) clips[actionId] = frames ?? ({} as DirectionFrames);
  return clips as unknown as PlayerFrames;
}

/** Load only the four clips needed to draw one equipment layer at first sight. */
export async function loadEquipItemLocomotionFrames(id: string): Promise<PlayerFrames> {
  const { manifests, urls } = equipMaps();
  const loaded = await Promise.all(
    LOCOMOTION_CLIP_IDS.map(async (actionId) => [
      actionId,
      await loadLazyEquipAction(id, actionId, manifests, urls),
    ] as const),
  );
  const clips: Record<string, DirectionFrames> = {};
  for (const [actionId, frames] of loaded) clips[actionId] = frames ?? ({} as DirectionFrames);
  return clips as unknown as PlayerFrames;
}

/**
 * Resolve the baked atlas URL one body clip decodes from, for residency bookkeeping
 * (issue #625): the {@link SpriteAtlasStore} acquires/releases a reference per URL so
 * a shared base atlas (a ghost body streams its living body's atlases) is unloaded
 * only after its final holder releases it. Mirrors {@link loadBodyActionFrames}'s
 * ghost→living mapping; `undefined` for a clip with no baked atlas.
 */
export function mobileAtlasUrlFor(body: number, actionId: string): string | undefined {
  const livingBody =
    body === GHOST_BODY_MALE ? MALE_BODY : body === GHOST_BODY_FEMALE ? FEMALE_BODY : body;
  return mobileAtlasMap.get(`human-${livingBody}-${actionId}`);
}

/** The equipment twin of {@link mobileAtlasUrlFor}: one layer clip's baked atlas URL. */
export function equipAtlasUrlFor(id: string, actionId: string): string | undefined {
  return equipMaps().urls.get(`${id}-${actionId}`);
}

/**
 * Evict one atlas from Pixi's asset cache, destroying its base texture. Called by the
 * residency layer ({@link AtlasRefs}) strictly after the final live reference to the
 * atlas released - never while any sprite still displays a frame of it. Removing the
 * cache entry is what makes a later {@link loadAction} for the same URL refetch and
 * decode a fresh texture (the evicted-clip reload path). Best-effort: a cache miss or
 * unload failure degrades silently.
 */
export function unloadAtlas(url: string): void {
  try {
    void Promise.resolve(Assets.unload(url)).catch(() => undefined);
  } catch {
    // Unloading is an optimization; never let cache cleanup break rendering.
  }
}

/** Load one equipment action on demand, preserving all placeholder degrade rules. */
export async function loadEquipItemActionFrames(
  id: string,
  actionId: string,
): Promise<DirectionFrames | undefined> {
  const { manifests, urls } = equipMaps();
  return loadLazyEquipAction(id, actionId, manifests, urls);
}
